import { useEffect, useState } from 'react';
import { FaFacebookMessenger, FaPen, FaSearch, FaTimes, FaUsers } from 'react-icons/fa';
import { isResponderMessengerEnabled, isResponderVoipEnabled } from '../config/voipFeatureFlag';
import { MdCall, MdLogin, MdLogout, MdMic } from 'react-icons/md';
import ResponderAlertModal from '../../../components/ResponderAlertModal';
import { useMessaging } from '../contexts/MessagingContext';
import { useVoice } from '../hooks/useVoice';
import { useMessengerFabPosition } from '../hooks/useMessengerFabPosition';
import { KEY_SYNC_ERROR_MESSAGE, getKeySyncReasonMessage } from '../utils/e2eeKeySyncReasons';
import { getConversationDisplayName, getConversationSubtitle } from '../utils/formatters';
import { canEditGroupName, getCurrentUserRole } from '../utils/permissions';
import { ConversationComposer } from './ConversationComposer';
import { AudioSettingsModal } from './AudioSettingsModal';
import { ConversationList, E2EEStatusIcon } from './ConversationList';
import { ConversationOptionsMenu } from './ConversationOptionsMenu';
import { EditableGroupName } from './EditableGroupName';
import { MessageInput } from './MessageInput';
import { MessageThread } from './MessageThread';
import { MessengerFAB } from './MessengerFAB';
import { ParticipantManager } from './ParticipantManager';
import { VoiceCallBanner } from './VoiceCallBanner';
import { VoiceCallOverlay } from './VoiceCallOverlay';

/**
 * Main messenger shell component.
 */
export function MessengerShell() {
  if (!isResponderMessengerEnabled()) return null;

  const voipEnabled = isResponderVoipEnabled();

  const {
    conversations,
    selectedConversation,
    selectedMessages,
    messagesByConversation,
    totalUnreadCount,
    currentUserId,
    conversationsLoading,
    messagesLoading,
    setOpenConversationForUnreadSuppression,
    selectConversation,
    createConversation,
    loadMessages,
    sendMessage,
    uploadMedia,
    deleteMessage,
    toggleMessageReaction,
    loadMessageContext,
    sendTypingIndicator,
    requestMissingEncryptionKeys,
    missingKeyConversationIds,
    keySyncStatusByConversation,
    keySyncReasonByConversation,
    clearSelectedConversationEncryptionKey,
    addParticipant,
    updateParticipantRole,
    transferOwnership,
    removeParticipant,
    updateConversationName,
    leaveConversation,
  } = useMessaging();

  const { callStatus, activeRoom, availableCalls, startCall, joinCall, leaveCall } = useVoice();

  const [isOpen, setIsOpen] = useState(false);
  const [showAudioSettings, setShowAudioSettings] = useState(false);
  const [showComposer, setShowComposer] = useState(false);
  const [showParticipants, setShowParticipants] = useState(false);
  const [isCreatingConversation, setIsCreatingConversation] = useState(false);
  const [conversationSearchQuery, setConversationSearchQuery] = useState('');
  const [conversationCreationError, setConversationCreationError] = useState('');
  const [replyingTo, setReplyingTo] = useState(null);
  const [showKeySyncPrompt, setShowKeySyncPrompt] = useState(false);

  const {
    fabPosition,
    isFabDragging,
    shellPosition,
    handleFabPointerDown,
    handleFabPointerMove,
    handleFabPointerEnd,
    consumeSuppressedClick,
  } = useMessengerFabPosition({ isOpen });

  const handleFABClick = () => {
    if (consumeSuppressedClick()) return;
    setIsOpen((prev) => !prev);
  };

  const handleNewConversationClick = () => {
    setShowComposer((prev) => !prev);
    setConversationSearchQuery('');
    setConversationCreationError(''); // Clear error when toggling composer
  };

  const handleCreateConversation = async (data) => {
    setIsCreatingConversation(true);
    setConversationCreationError(''); // Clear previous errors
    try {
      const newConversation = await createConversation(data);
      if (newConversation) {
        selectConversation(newConversation.id);
        setShowComposer(false);
        setConversationCreationError('');
      }
    } catch (err) {
      console.error('[MessengerShell] Failed to create conversation:', err);
      // Extract error message from the error object
      const errorMessage =
        err?.response?.data?.message ||
        err?.message ||
        'Failed to create conversation. Please try again.';
      setConversationCreationError(errorMessage);
    } finally {
      setIsCreatingConversation(false);
    }
  };

  const handleSendMessage = async (text, replyId) => {
    if (!selectedConversation) return;
    await sendMessage(text, replyId);
    setReplyingTo(null); // Clear reply state after sending
  };

  const handleUploadMedia = async (file, caption) => {
    if (!selectedConversation) return;
    await uploadMedia(file, caption);
  };

  const handleTyping = (isTyping) => {
    if (!selectedConversation) return;
    sendTypingIndicator(selectedConversation.id, isTyping);
  };

  const handleReply = (message) => {
    setReplyingTo(message);
  };

  const handleCancelReply = () => {
    setReplyingTo(null);
  };

  const selectedConversationKey =
    selectedConversation?.id == null ? null : `${selectedConversation.id}`;
  const selectedKeySyncStatus =
    selectedConversationKey == null
      ? null
      : keySyncStatusByConversation?.[selectedConversationKey] || null;
  const selectedKeySyncReason =
    selectedConversationKey == null
      ? null
      : keySyncReasonByConversation?.[selectedConversationKey] || null;
  const selectedHasMissingKey =
    selectedConversationKey != null &&
    (missingKeyConversationIds || []).some((id) => `${id}` === selectedConversationKey);
  const selectedMessagesNeedKey = selectedMessages.some((message) =>
    ['pending-key', 'failed'].includes(message?.__e2ee?.status),
  );
  const showEncryptionKeySyncBanner =
    Boolean(selectedConversationKey) &&
    (selectedHasMissingKey ||
      selectedMessagesNeedKey ||
      Boolean(selectedKeySyncStatus) ||
      Boolean(selectedKeySyncReason));
  const isSelectedEncryptionKeySynced =
    Boolean(selectedConversationKey) && !showEncryptionKeySyncBanner;
  const selectedKeySyncReasonMessage =
    selectedKeySyncStatus === 'error'
      ? KEY_SYNC_ERROR_MESSAGE
      : getKeySyncReasonMessage(selectedKeySyncReason);

  const handleRequestKeySync = () => {
    if (!selectedConversation) return;
    setShowKeySyncPrompt(true);
  };

  const handleConfirmKeySyncPrompt = async () => {
    setShowKeySyncPrompt(false);
    try {
      await requestMissingEncryptionKeys();
    } catch (error) {
      console.error('[MessengerShell] Failed to request encryption key sync:', error);
    }
  };

  const activeCallForConversation = availableCalls.find(
    (c) => `${c.conversationId}` === `${selectedConversation?.id}`,
  );

  const isEndingThisCall =
    callStatus === 'in-call' &&
    activeRoom?.conversationId != null &&
    `${activeRoom.conversationId}` === `${selectedConversation?.id}`;

  // Show join UI whenever a room is open for this conversation, even if currently in another call
  const isJoiningThisCall = !isEndingThisCall && !!activeCallForConversation;

  const callButtonDisabled = !isEndingThisCall && callStatus !== 'idle';

  const handleCallButtonClick = () => {
    if (!voipEnabled) return;
    if (!selectedConversation) return;
    if (isEndingThisCall) {
      leaveCall();
    } else if (isJoiningThisCall) {
      joinCall(
        activeCallForConversation.roomId,
        selectedConversation.id,
        activeCallForConversation.createdAt,
      );
    } else if (callStatus === 'idle') {
      const displayName =
        selectedConversation.name ||
        getConversationDisplayName(selectedConversation, currentUserId);
      startCall(selectedConversation.id, displayName);
    }
  };

  useEffect(() => {
    const conversationId = isOpen ? selectedConversation?.id : null;
    setOpenConversationForUnreadSuppression(conversationId);
  }, [
    isOpen,
    selectedConversation?.id,
    selectedMessages.length,
    setOpenConversationForUnreadSuppression,
  ]);

  useEffect(() => {
    if (typeof window === 'undefined') return undefined;

    const handleOpenMessenger = () => {
      setShowComposer(false);
      setIsOpen(true);
    };

    window.addEventListener('responder-messenger-open', handleOpenMessenger);
    return () => {
      window.removeEventListener('responder-messenger-open', handleOpenMessenger);
    };
  }, []);

  return (
    <>
      {voipEnabled && <VoiceCallOverlay />}
      {voipEnabled && <VoiceCallBanner />}
      {showAudioSettings && (
        <AudioSettingsModal
          onClose={() => setShowAudioSettings(false)}
          conversationId={selectedConversation?.id ?? null}
        />
      )}
      <ResponderAlertModal
        show={showKeySyncPrompt}
        title="Sync encryption key"
        confirmLabel="OK, request key sync"
        cancelLabel="Cancel"
        confirmVariant="primary"
        cancelVariant="secondary"
        onConfirm={handleConfirmKeySyncPrompt}
        onCancel={() => setShowKeySyncPrompt(false)}
      >
        <div className="d-flex flex-column gap-2">
          <p className="mb-0">{selectedKeySyncReasonMessage}</p>
          <p className="mb-0">
            Open Messenger on a device that can already read this conversation. Once the messages
            are open there, press OK here to request key sync.
          </p>
        </div>
      </ResponderAlertModal>
      <MessengerFAB
        isOpen={isOpen}
        unreadCount={totalUnreadCount}
        onClick={handleFABClick}
        position={fabPosition}
        onPointerDown={handleFabPointerDown}
        onPointerMove={handleFabPointerMove}
        onPointerUp={handleFabPointerEnd}
        isDragging={isFabDragging}
      />

      {isOpen && (
        <aside
          className="responder-messenger-shell"
          role="dialog"
          aria-modal="false"
          style={{
            left: `${shellPosition.left}px`,
            right: 'auto',
            top: `${shellPosition.top}px`,
            width: `${shellPosition.width}px`,
            height: `${shellPosition.height}px`,
            bottom: 'auto',
          }}
        >
          {/* Sidebar: Conversation List or Composer */}
          <section className="responder-messenger-sidebar">
            <header className="responder-messenger-sidebar__header">
              <div className="responder-messenger-title-wrap">
                <h6 className="responder-messenger-title">Responder Messages</h6>
              </div>
              <div className="responder-messenger-header-actions">
                <button
                  type="button"
                  className="responder-messenger-icon-btn"
                  aria-label="Messenger settings"
                  title="Messenger settings"
                  onClick={() => setShowAudioSettings(true)}
                >
                  <MdMic size={14} />
                </button>
                <button
                  type="button"
                  className="responder-messenger-icon-btn"
                  aria-label="Create message"
                  onClick={handleNewConversationClick}
                >
                  <FaPen size={12} />
                </button>
                <button
                  type="button"
                  className="responder-messenger-icon-btn"
                  aria-label="Close messenger"
                  onClick={() => setIsOpen(false)}
                >
                  <FaTimes size={12} />
                </button>
              </div>
            </header>

            {!showComposer ? (
              <>
                <div className="responder-messenger-search">
                  <FaSearch size={11} />
                  <input
                    value={conversationSearchQuery}
                    onChange={(e) => setConversationSearchQuery(e.target.value)}
                    type="text"
                    placeholder="Search chats"
                  />
                </div>

                <ConversationList
                  conversations={conversations}
                  selectedId={selectedConversation?.id}
                  currentUserId={currentUserId}
                  onSelectConversation={(id) => {
                    selectConversation(id);
                    setShowComposer(false);
                  }}
                  loading={conversationsLoading}
                  searchQuery={conversationSearchQuery}
                  liveCallConversationId={activeRoom?.conversationId ?? null}
                  messagesByConversation={messagesByConversation}
                  keySyncStatusByConversation={keySyncStatusByConversation}
                  keySyncReasonByConversation={keySyncReasonByConversation}
                  missingKeyConversationIds={missingKeyConversationIds}
                />
              </>
            ) : (
              <ConversationComposer
                onCreateConversation={handleCreateConversation}
                isCreating={isCreatingConversation}
                onCancel={() => setShowComposer(false)}
                error={conversationCreationError}
                currentUserId={currentUserId}
              />
            )}
          </section>

          {/* Thread: Message Display and Input */}
          <section className="responder-messenger-thread">
            {selectedConversation ? (
              <>
                <header className="responder-messenger-thread__header">
                  <div className="responder-messenger-thread__header-title">
                    <div className="responder-messenger-thread__header-name">
                      <E2EEStatusIcon
                        conversationId={selectedConversation.id}
                        keySyncStatusByConversation={keySyncStatusByConversation}
                        keySyncReasonByConversation={keySyncReasonByConversation}
                        missingKeyConversationIds={missingKeyConversationIds}
                      />
                      {selectedConversation.type === 'group' ? (
                        <EditableGroupName
                          name={selectedConversation.name || 'Group Chat'}
                          onSave={updateConversationName}
                          canEdit={canEditGroupName(
                            getCurrentUserRole(selectedConversation, currentUserId).role,
                            selectedConversation,
                          )}
                        />
                      ) : (
                        <h6 className="mb-0">
                          {getConversationDisplayName(selectedConversation, currentUserId)}
                        </h6>
                      )}
                    </div>
                    <small className="text-muted">
                      {getConversationSubtitle(selectedConversation) ||
                        (selectedConversation.type === 'group'
                          ? 'Group conversation'
                          : 'Direct conversation')}
                    </small>
                  </div>
                  <div>
                    {voipEnabled && (
                      <button
                        type="button"
                        className="responder-messenger-icon-btn"
                        aria-label={
                          isEndingThisCall
                            ? 'Leave call'
                            : isJoiningThisCall
                              ? 'Join call'
                              : 'Start call'
                        }
                        title={
                          isEndingThisCall
                            ? 'Leave call'
                            : isJoiningThisCall
                              ? 'Join call'
                              : 'Start call'
                        }
                        onClick={handleCallButtonClick}
                        disabled={callButtonDisabled}
                        style={
                          isEndingThisCall
                            ? { color: '#e53e3e' }
                            : isJoiningThisCall
                              ? {
                                  color: '#38a169',
                                  animation: 'responder-pulse 1.4s ease-in-out infinite',
                                }
                              : callButtonDisabled
                                ? { opacity: 0.4, cursor: 'not-allowed' }
                                : {}
                        }
                      >
                        {isEndingThisCall ? (
                          <MdLogout size={16} />
                        ) : isJoiningThisCall ? (
                          <MdLogin size={16} />
                        ) : (
                          <MdCall size={16} />
                        )}
                      </button>
                    )}
                    {selectedConversation.type === 'group' && (
                      <button
                        type="button"
                        className="responder-messenger-icon-btn"
                        aria-label="Manage participants"
                        onClick={() => setShowParticipants((prev) => !prev)}
                        title="Manage participants"
                      >
                        <FaUsers size={14} />
                      </button>
                    )}
                    <ConversationOptionsMenu
                      conversation={selectedConversation}
                      currentUserId={currentUserId}
                      onLeaveConversation={leaveConversation}
                    />
                  </div>
                </header>

                {showParticipants && selectedConversation.type === 'group' ? (
                  <ParticipantManager
                    conversation={selectedConversation}
                    currentUserId={currentUserId}
                    onAddParticipant={addParticipant}
                    onUpdateRole={updateParticipantRole}
                    onTransferOwnership={transferOwnership}
                    onRemoveParticipant={removeParticipant}
                  />
                ) : (
                  <>
                    <MessageThread
                      messages={selectedMessages}
                      currentUserId={currentUserId}
                      participants={selectedConversation.participants || []}
                      conversationId={selectedConversation.id}
                      onDeleteMessage={deleteMessage}
                      onToggleReaction={toggleMessageReaction}
                      onReply={handleReply}
                      loadMessages={loadMessages}
                      loadMessageContext={loadMessageContext}
                      loading={messagesLoading}
                      showEncryptionKeySyncBanner={showEncryptionKeySyncBanner}
                      isEncryptionKeySynced={isSelectedEncryptionKeySynced}
                      keySyncStatus={
                        selectedKeySyncStatus || (selectedHasMissingKey ? 'missing' : null)
                      }
                      keySyncReason={selectedKeySyncReason}
                      onRequestKeySync={handleRequestKeySync}
                      onClearEncryptionKey={clearSelectedConversationEncryptionKey}
                    />

                    <MessageInput
                      onSendMessage={handleSendMessage}
                      onUploadMedia={handleUploadMedia}
                      onTyping={handleTyping}
                      replyingTo={replyingTo}
                      onCancelReply={handleCancelReply}
                    />
                  </>
                )}
              </>
            ) : (
              <div className="responder-messenger-thread__empty">
                <FaFacebookMessenger size={24} />
                <p className="mb-0">Select a chat or start a new one.</p>
              </div>
            )}
          </section>
        </aside>
      )}
    </>
  );
}
